import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Menu,
  CreditCard,
  Lock,
  CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";
import { authFetch } from "@/lib/authFetch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UpdateBilling = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    cardholder_name: "",
    card_number: "",
    expiration: "",
    cvv: "",
    billing_zip: "",
  });

  const formatCardNumber = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };

  const formatExpiration = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
  };

  const handleChange = (field, value) => {
    if (field === "card_number") value = formatCardNumber(value);
    if (field === "expiration") value = formatExpiration(value);
    if (field === "cvv") value = value.replace(/\D/g, "").slice(0, 4);
    if (field === "billing_zip") value = value.replace(/\D/g, "").slice(0, 5);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isValid =
    form.cardholder_name.trim().length > 1 &&
    form.card_number.replace(/\s/g, "").length >= 15 &&
    form.expiration.length === 5 &&
    form.cvv.length >= 3 &&
    form.billing_zip.length === 5;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    setSubmitting(true);
    try {
      const response = await authFetch(`${API}/clients/me/update-card`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cardholder_name: form.cardholder_name.trim(),
          card_number: form.card_number.replace(/\s/g, ""),
          expiration: form.expiration,
          billing_zip: form.billing_zip,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("Card updated successfully");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update card");
      }
    } catch {
      toast.error("Failed to update card. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`flex min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-black" : "bg-slate-50"}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isClient={true} />

      <main className="flex-1 min-w-0 md:ml-64">
        <header className="header-glass sticky top-0 z-30 px-4 md:px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <h1 className={`text-xl md:text-2xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                Update Billing Card
              </h1>
              <p className={`text-sm ${isDarkMode ? "text-zinc-400" : "text-slate-500"}`}>
                Update your debit or credit card on file
              </p>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6 max-w-lg mx-auto">
          {submitted ? (
            <Card className={isDarkMode ? "bg-zinc-900 border-zinc-800" : ""} data-testid="card-success">
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
                <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                  Card Updated Successfully
                </h2>
                <p className={`text-sm ${isDarkMode ? "text-zinc-400" : "text-slate-500"}`}>
                  Your new billing information has been submitted. Your account will be updated shortly.
                </p>
                <Button
                  className="mt-6 bg-[#357c2a] hover:bg-[#2d6a5d] text-white"
                  onClick={() => {
                    setSubmitted(false);
                    setForm({ cardholder_name: "", card_number: "", expiration: "", cvv: "", billing_zip: "" });
                  }}
                  data-testid="submit-another-btn"
                >
                  Update Another Card
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className={isDarkMode ? "bg-zinc-900 border-zinc-800" : ""} data-testid="card-form">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#357c2a] to-[#265324] flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className={isDarkMode ? "text-white" : ""}>Card Information</CardTitle>
                    <CardDescription>Enter your new debit or credit card details</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="cardholder_name" className={isDarkMode ? "text-zinc-300" : ""}>
                      Cardholder Name
                    </Label>
                    <Input
                      id="cardholder_name"
                      placeholder="John Doe"
                      value={form.cardholder_name}
                      onChange={(e) => handleChange("cardholder_name", e.target.value)}
                      className={isDarkMode ? "bg-zinc-800 border-zinc-700 text-white" : ""}
                      data-testid="cardholder-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="card_number" className={isDarkMode ? "text-zinc-300" : ""}>
                      Card Number
                    </Label>
                    <Input
                      id="card_number"
                      placeholder="1234 5678 9012 3456"
                      value={form.card_number}
                      onChange={(e) => handleChange("card_number", e.target.value)}
                      className={isDarkMode ? "bg-zinc-800 border-zinc-700 text-white" : ""}
                      data-testid="card-number-input"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label htmlFor="expiration" className={isDarkMode ? "text-zinc-300" : ""}>
                        Expiration
                      </Label>
                      <Input
                        id="expiration"
                        placeholder="MM/YY"
                        value={form.expiration}
                        onChange={(e) => handleChange("expiration", e.target.value)}
                        className={isDarkMode ? "bg-zinc-800 border-zinc-700 text-white" : ""}
                        data-testid="expiration-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cvv" className={isDarkMode ? "text-zinc-300" : ""}>
                        CVV
                      </Label>
                      <Input
                        id="cvv"
                        placeholder="123"
                        type="password"
                        value={form.cvv}
                        onChange={(e) => handleChange("cvv", e.target.value)}
                        className={isDarkMode ? "bg-zinc-800 border-zinc-700 text-white" : ""}
                        data-testid="cvv-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="billing_zip" className={isDarkMode ? "text-zinc-300" : ""}>
                        Billing Zip
                      </Label>
                      <Input
                        id="billing_zip"
                        placeholder="79701"
                        value={form.billing_zip}
                        onChange={(e) => handleChange("billing_zip", e.target.value)}
                        className={isDarkMode ? "bg-zinc-800 border-zinc-700 text-white" : ""}
                        data-testid="billing-zip-input"
                      />
                    </div>
                  </div>

                  <div className={`flex items-center gap-2 text-xs ${isDarkMode ? "text-zinc-500" : "text-slate-400"}`}>
                    <Lock className="h-3 w-3" />
                    Your card details are securely transmitted and never stored in full.
                  </div>

                  <Button
                    type="submit"
                    disabled={!isValid || submitting}
                    className="w-full bg-[#357c2a] hover:bg-[#2d6a5d] text-white"
                    data-testid="submit-card-btn"
                  >
                    {submitting ? "Submitting..." : "Update Card"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default UpdateBilling;
